import React, { useState, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Star, MapPin, Wifi, Coffee, Car, Wind, Tv, Users, ChevronLeft, ChevronRight, Share2, Heart, ShieldCheck, Waves, Dumbbell, Wine, Utensils, Zap, Link2, CheckCircle, Plus, Minus } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { DateRange } from 'react-date-range';

import { propertyService } from '../services/propertyService';
import DefaultSEO from '../components/DefaultSEO';
import LoginModal from '../components/LoginModal';

const getAmenityIcon = (label) => {
    const lowerLabel = label.toLowerCase();
    if (lowerLabel.includes('wifi')) return <Wifi size={18} />;
    if (lowerLabel.includes('parking')) return <Car size={18} />;
    if (lowerLabel.includes('ac') || lowerLabel.includes('air conditioning')) return <Wind size={18} />;
    if (lowerLabel.includes('tv') || lowerLabel.includes('television')) return <Tv size={18} />;
    if (lowerLabel.includes('breakfast') || lowerLabel.includes('coffee')) return <Coffee size={18} />;
    if (lowerLabel.includes('restaurant') || lowerLabel.includes('dining')) return <Utensils size={18} />;
    if (lowerLabel.includes('cctv') || lowerLabel.includes('security')) return <ShieldCheck size={18} />;
    if (lowerLabel.includes('pool') || lowerLabel.includes('swimming')) return <Waves size={18} />;
    if (lowerLabel.includes('gym') || lowerLabel.includes('fitness')) return <Dumbbell size={18} />;
    if (lowerLabel.includes('bar') || lowerLabel.includes('lounge')) return <Wine size={18} />;
    if (lowerLabel.includes('spa') || lowerLabel.includes('massage')) return <Heart size={18} />;
    if (lowerLabel.includes('power') || lowerLabel.includes('backup')) return <Zap size={18} />;
    return <Star size={18} />;
};

const mapPropertyData = (data, passedIsSoldOut = false) => {
    return {
        id: data.id,
        name: data.name,
        rating: Number(data.star_rating) || 4.2,
        reviews: Math.floor(Math.random() * 500) + 100, // mock count
        location: data.address ? `${data.address}, ${data.city}, ${data.state}` : `${data.city}, ${data.state}`,
        description: `Experience a vibrant stay at ${data.name}. Located strategically in ${data.city}, we offer extensive amenities catering to all your travel requirements!`,
        images: (data.images && data.images.length > 0)
            ? data.images.map(img => {
                if (typeof img === 'string') {
                    return img.startsWith('http') ? img : `${process.env.REACT_APP_STORAGE_URL || (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/api\/v1\/?$/, '') + '/storage'}/${img}`;
                }
                const path = img?.file_path || img?.url || '';
                if (!path) return "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80";
                return path.startsWith('http') ? path : `${process.env.REACT_APP_STORAGE_URL || (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/api\/v1\/?$/, '') + '/storage'}/${path}`;
            })
            : [
                "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800&q=80",
                "https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800&q=80"
            ],
        categorizedImages: data.images ? data.images.reduce((acc, img) => {
            const cat = typeof img === 'object' ? (img.category || 'other') : 'other';
            if (!acc[cat]) acc[cat] = [];
            const path = typeof img === 'string' ? img : (img?.file_path || img?.url || '');
            if (path) {
                acc[cat].push(path.startsWith('http') ? path : `${process.env.REACT_APP_STORAGE_URL || (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/api\/v1\/?$/, '') + '/storage'}/${path}`);
            }
            return acc;
        }, {}) : {},
        amenities: Array.isArray(data.amenities) ? data.amenities.map(a => ({
            icon: getAmenityIcon(typeof a === 'string' ? a : (a?.label || '')),
            label: typeof a === 'string' ? a : (a?.label || '')
        })) : [],
        rooms: (data.rooms && data.rooms.length > 0) ? data.rooms.map(room => {
            const roomImage = (room.images && room.images.length > 0)
                ? (room.images[0].startsWith('http') ? room.images[0] : `${process.env.REACT_APP_STORAGE_URL || (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1').replace(/\/api\/v1\/?$/, '') + '/storage'}/${room.images[0]}`)
                : (room.image || "https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800&q=80");

            const maxAdults = parseInt(room.max_adults) || 2;
            const maxChildren = parseInt(room.max_children) || 0;
            const isSoldOut = data.is_sold_out === true || passedIsSoldOut || room.is_sold_out === true || (room.available_rooms !== undefined && parseInt(room.available_rooms) <= 0) || (parseInt(room.total_rooms_available) <= 0);

            return {
                id: room.id,
                type: room.room_type_name || room.name || room.room_type || 'Standard Room',
                capacity: maxAdults + maxChildren,
                pricePerNight: Number(room.base_price_per_night),
                book_hash: room.book_hash || null,
                features: [
                    `Max ${maxAdults} Adults`,
                    `Max ${maxChildren} Children`
                ],
                image: roomImage,
                isSoldOut: isSoldOut
            };
        }).sort((a, b) => {
            if (a.isSoldOut === b.isSoldOut) return 0;
            return a.isSoldOut ? 1 : -1;
        }) : []
    };
};

const HotelDetailsPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [hotel, setHotel] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedRoom, setSelectedRoom] = useState(null);
    const [showLoginModal, setShowLoginModal] = useState(false);

    React.useEffect(() => {
        const loadProperty = async () => {
            setIsLoading(true);
            try {
                const response = await propertyService.getPropertyDetail(id);
                // Unwrap dynamic payload format 
                const propertyPayload = response.data && response.data.id ? response.data : response;

                if (propertyPayload.provider !== 'ratehawk' && !String(propertyPayload.id).startsWith('rh_') && propertyPayload.status && propertyPayload.status !== 'active' && propertyPayload.status !== 'approved' && propertyPayload.status !== 'verified') {
                    setHotel(null);
                    return;
                }

                const passedIsSoldOut = location.state?.isSoldOut || false;
                const structuredHotel = mapPropertyData(propertyPayload, passedIsSoldOut);
                setHotel(structuredHotel);
                if (structuredHotel.rooms.length > 0) {
                    const availableRoom = structuredHotel.rooms.find(r => !r.isSoldOut) || structuredHotel.rooms[0];
                    setSelectedRoom(availableRoom);
                }
            } catch (err) {
                console.error("Failed to load property details", err);
            } finally {
                setIsLoading(false);
            }
        };

        if (id) {
            loadProperty();
        }
    }, [id]);

    const [onlineDiscount, setOnlineDiscount] = useState({ percentage: 0, max_amount: 0, status: 'active' });

    React.useEffect(() => {
        const fetchDiscount = async () => {
            try {
                const apiBase = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/api/v1';
                const cleanApiBase = apiBase.replace(/\/+$/, '');
                const res = await fetch(`${cleanApiBase}/settings/online-discount`);
                const data = await res.json();
                if (data.success && data.data) {
                    setOnlineDiscount(data.data);
                }
            } catch (e) {
                // silent
            }
        };
        fetchDiscount();
    }, []);

    const [showDatePicker, setShowDatePicker] = useState(false);
    const [dateRange, setDateRange] = useState(() => {
        const params = new URLSearchParams(location.search);
        const checkinStr = params.get("checkin");
        const checkoutStr = params.get("checkout");
        if (checkinStr && checkoutStr) {
            return [{
                startDate: new Date(checkinStr + 'T00:00:00'),
                endDate: new Date(checkoutStr + 'T00:00:00'),
                key: 'selection'
            }];
        }
        return [{
            startDate: new Date(),
            endDate: addDays(new Date(), 1),
            key: 'selection'
        }];
    });
    const [guests, setGuests] = useState(() => {
        const params = new URLSearchParams(location.search);
        return parseInt(params.get("guests") || "2", 10);
    });
    const [activeImageIdx, setActiveImageIdx] = useState(0);
    const [activeCategory, setActiveCategory] = useState('All');
    const [isWishlisted, setIsWishlisted] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const scrollContainerRef = useRef(null);

    // Compute images to show based on selected category
    const displayImages = hotel ? (activeCategory === 'All' ? hotel.images : (hotel.categorizedImages[activeCategory] || [])) : [];


    const handleScroll = (e) => {
        const container = e.target;
        const scrollPosition = container.scrollLeft;
        const width = container.clientWidth;
        if (width > 0 && hotel) {
            const newIdx = Math.round(scrollPosition / width);
            if (newIdx !== activeImageIdx && newIdx >= 0 && newIdx < displayImages.length) {
                setActiveImageIdx(newIdx);
            }
        }
    };

    const scrollToImage = (idx) => {
        setActiveImageIdx(idx);
        if (scrollContainerRef.current) {
            const width = scrollContainerRef.current.clientWidth;
            scrollContainerRef.current.scrollTo({
                left: idx * width,
                behavior: 'smooth'
            });
        }
    };

    const handleNextImage = () => {
        if (!hotel || displayImages.length === 0) return;
        const nextIdx = (activeImageIdx + 1) % displayImages.length;
        scrollToImage(nextIdx);
    };

    const handlePrevImage = () => {
        if (!hotel || displayImages.length === 0) return;
        const prevIdx = (activeImageIdx - 1 + displayImages.length) % displayImages.length;
        scrollToImage(prevIdx);
    };

    const getDaysDifference = () => {
        const start = dateRange[0].startDate;
        const end = dateRange[0].endDate;
        return Math.max(1, Math.ceil((end - start) / (1000 * 60 * 60 * 24)));
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-50">
                <div className="w-10 h-10 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!hotel) {
        return (
            <div className="flex justify-center items-center h-screen bg-gray-50">
                <h3 className="text-xl font-semibold text-gray-700">Hotel unavailable</h3>
            </div>
        );
    }

    const totalNights = getDaysDifference();
    const totalPrice = selectedRoom ? selectedRoom.pricePerNight * totalNights : 0;
    const taxes = selectedRoom ? totalPrice * 0.18 : 0;
    const finalAmount = selectedRoom ? totalPrice + taxes : 0;

    const handleBookNow = () => {
        if (!hotel || !selectedRoom) return;
        const token = localStorage.getItem('auth_token');
        if (!token) {
            setShowLoginModal(true);
            return;
        }
        const { amenities, ...serializableHotel } = hotel;
        const serializableAmenities = amenities.map(({ label }) => ({ label }));
        navigate(`/checkout/${id}`, {
            state: {
                hotel: { ...serializableHotel, amenities: serializableAmenities },
                room: selectedRoom,
                dates: dateRange[0],
                guests: guests,
                pricing: { totalNights, totalPrice, taxes, finalAmount }
            }
        });
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(window.location.href);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    };

    return (
        <div className="bg-[#F8FAFC] min-h-screen pb-40 lg:pb-0">
            <DefaultSEO 
                title={`${hotel.name} - Book Now`}
                description={hotel.description || `Book ${hotel.name} situated in ${hotel.location?.city || 'India'}. Discover great amenities and comfortable stays.`}
                ogTitle={`${hotel.name} | ZoyoTrip`}
                ogDescription={hotel.description || `Book your stay at ${hotel.name}.`}
                ogImage={hotel.images?.[0]?.url || ''}
            >
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "Hotel",
                        "name": hotel.name,
                        "description": hotel.description,
                        "image": hotel.images?.map(img => img.url) || [],
                        "address": {
                            "@type": "PostalAddress",
                            "addressLocality": hotel.location?.city,
                            "addressRegion": hotel.location?.state,
                            "postalCode": hotel.location?.pincode,
                            "streetAddress": hotel.location?.address
                        },
                        "priceRange": `₹${hotel.startingPrice || 1000} - ₹${(hotel.startingPrice || 1000) * 3}`
                    })}
                </script>
            </DefaultSEO>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">

                {/* Header & Typography */}
                <div className="mb-4">
                    <h1 className="text-2xl md:text-4xl font-black text-gray-900 tracking-tight capitalize">{hotel.name}</h1>
                </div>

                {/* Image Gallery */}
                <div className="mb-4">
                    {/* Category Tabs */}
                    {Object.keys(hotel.categorizedImages || {}).length > 0 && (
                        <div className="flex gap-2 overflow-x-auto scrollbar-hide mb-4 pb-2">
                            <button
                                onClick={() => { setActiveCategory('All'); setActiveImageIdx(0); }}
                                className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${activeCategory === 'All' ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                            >
                                All Photos
                            </button>
                            {Object.keys(hotel.categorizedImages).map(cat => (
                                <button
                                    key={cat}
                                    onClick={() => { setActiveCategory(cat); setActiveImageIdx(0); }}
                                    className={`px-4 py-1.5 rounded-full text-sm font-semibold whitespace-nowrap transition-colors capitalize ${activeCategory === cat ? 'bg-blue-600 text-white' : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                                >
                                    {cat.replace('_', ' ')}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-6">
                    {/* Main Image Slider */}
                    <div className="md:col-span-2 relative rounded-3xl overflow-hidden shadow-sm h-[300px] md:h-[450px] group">
                        <div
                            ref={scrollContainerRef}
                            onScroll={handleScroll}
                            className="flex overflow-x-auto snap-x snap-mandatory scrollbar-hide h-full w-full"
                        >
                            {displayImages.map((img, idx) => (
                                <div key={idx} className="w-full h-full flex-shrink-0 snap-start relative">
                                    <img src={img} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" alt={`Hotel view ${idx + 1}`} />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent"></div>
                                </div>
                            ))}
                        </div>

                        {/* Top-Right Overlays (Solid White) */}
                        <div className="absolute top-4 right-4 flex gap-2 z-10">
                            <button
                                onClick={handleCopyLink}
                                className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-gray-800 hover:bg-gray-100 transition-colors shadow-md active:scale-95"
                                title="Copy Link"
                            >
                                <Link2 size={18} />
                            </button>
                            <button
                                onClick={() => setIsWishlisted(!isWishlisted)}
                                className="w-10 h-10 rounded-full bg-white flex items-center justify-center text-gray-800 hover:bg-gray-100 transition-colors shadow-md active:scale-95"
                            >
                                <Heart size={18} fill={isWishlisted ? "#ef4444" : "none"} className={isWishlisted ? "text-red-500" : "text-gray-800"} />
                            </button>
                        </div>

                        {/* Desktop Navigation Arrows */}
                        <button onClick={handlePrevImage} className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white items-center justify-center text-gray-800 hover:bg-gray-100 transition-colors shadow-md z-10">
                            <ChevronLeft size={24} />
                        </button>
                        <button onClick={handleNextImage} className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white items-center justify-center text-gray-800 hover:bg-gray-100 transition-colors shadow-md z-10">
                            <ChevronRight size={24} />
                        </button>

                        {/* Counter Pill */}
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-md border border-white/20 text-white px-3 py-1 rounded-full text-xs font-bold z-10 tracking-widest shadow-lg">
                            {activeImageIdx + 1} / {displayImages.length}
                        </div>
                    </div>

                    {/* Desktop Side Images */}
                    <div className="hidden md:flex flex-col gap-4">
                        {displayImages.slice(1, 3).map((img, idx) => (
                            <div key={idx} className="relative rounded-3xl overflow-hidden h-[217px] shadow-sm group cursor-pointer" onClick={() => scrollToImage(idx + 1)}>
                                <img src={img} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" alt={`Side view ${idx}`} />
                                <div className="absolute inset-0 bg-black/10 group-hover:bg-transparent transition-colors"></div>
                                
                                {/* Overlay for additional photos if more than 3 total */}
                                {idx === 1 && displayImages.length > 3 && (
                                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center group-hover:bg-black/60 transition-colors">
                                        <span className="text-white font-bold text-lg tracking-wide">
                                            +{displayImages.length - 3} Photos
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Mobile Image Thumbnails */}
                    <div className="md:hidden flex gap-2 overflow-x-auto scrollbar-hide py-1 mt-1">
                        {displayImages.map((img, idx) => (
                            <div
                                key={idx}
                                onClick={() => scrollToImage(idx)}
                                className={`flex-shrink-0 w-[72px] h-[54px] rounded-xl overflow-hidden cursor-pointer border-2 transition-all duration-300 ${activeImageIdx === idx ? 'border-blue-500 shadow-sm' : 'border-transparent opacity-70 hover:opacity-100'}`}
                            >
                                <img src={img} className="w-full h-full object-cover" alt={`Thumb ${idx}`} />
                            </div>
                        ))}
                    </div>
                </div>

                {/* Rating & Location (Moved Below Images) */}
                <div className="flex flex-col sm:flex-row sm:items-center text-sm text-gray-600 gap-y-3 gap-x-4 mb-8">
                    <div className="flex items-center bg-white border border-gray-200 px-3 py-1.5 rounded-xl shadow-sm w-fit">
                        <Star className="text-yellow-400 fill-yellow-400 mr-1.5" size={18} />
                        <span className="font-bold text-gray-900 mr-1.5 text-base">{hotel.rating}</span>
                        <span className="text-gray-500 text-sm">({hotel.reviews} reviews)</span>
                    </div>
                    <div className="hidden sm:block text-gray-300">•</div>
                    <div className="flex items-start sm:items-center">
                        <MapPin size={20} className="mr-2 text-blue-500 flex-shrink-0 mt-0.5 sm:mt-0" />
                        <span className="leading-snug font-medium text-gray-700 text-base">{hotel.location}</span>
                    </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-8">
                    {/* Left Column: Details */}
                    <div className="lg:w-2/3">
                        {/* About Section */}
                        <div className="bg-white p-3 sm:p-6 rounded-3xl shadow-sm border border-gray-100 mb-6 hover:shadow-md transition-shadow">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">About this hotel</h2>
                            <p className="text-gray-600 leading-relaxed text-sm md:text-base font-medium">{hotel.description}</p>
                        </div>

                        {/* Amenities Section */}
                        <div className="bg-white p-3 sm:p-6 rounded-3xl shadow-sm border border-gray-100 mb-6 hover:shadow-md transition-shadow">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Top Amenities</h2>
                            <div className="flex flex-wrap gap-3">
                                {hotel.amenities.map((amenity, idx) => (
                                    <div key={idx} className="flex items-center gap-2 bg-gray-50 border border-gray-100 px-3.5 py-2 rounded-xl hover:bg-blue-50 hover:border-blue-100 transition-colors">
                                        <div className="text-blue-500">
                                            {amenity.icon}
                                        </div>
                                        <span className="text-[13px] font-semibold text-gray-700">{amenity.label}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Room Selection */}
                        <div className="mb-6">
                            <h2 className="text-xl font-bold text-gray-900 mb-4 px-2">Select your room</h2>
                            {hotel.rooms.length > 0 ? (
                                <div className="space-y-4">
                                    {hotel.rooms.map((room) => (
                                        <div
                                            key={room.id}
                                            className={`bg-white rounded-3xl border-2 transition-all cursor-pointer overflow-hidden flex flex-col sm:flex-row sm:min-h-[170px] group ${room.isSoldOut ? 'opacity-60 grayscale bg-gray-50' : ''} ${selectedRoom?.id === room.id ? 'border-blue-500 shadow-md ring-4 ring-blue-50' : 'border-gray-100 shadow-sm hover:border-blue-300'}`}
                                            onClick={() => setSelectedRoom(room)}
                                        >
                                            <div className="w-full sm:w-56 md:w-64 h-48 sm:h-auto relative overflow-hidden shrink-0 bg-gray-100">
                                                <img 
                                                    src={room.image || "https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800&q=80"} 
                                                    alt={room.type} 
                                                    className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 group-hover:scale-105" 
                                                    onError={(e) => {
                                                        e.target.onerror = null;
                                                        e.target.src = "https://images.unsplash.com/photo-1590490360182-c33d57733427?w=800&q=80";
                                                    }}
                                                />
                                                {selectedRoom?.id === room.id && (
                                                    <div className="absolute top-3 left-3 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md z-10">
                                                        Selected
                                                    </div>
                                                )}
                                            </div>
                                            <div className="p-4 sm:p-5 flex-1 flex flex-col justify-between">
                                                <div>
                                                    <h3 className="text-lg font-bold text-gray-900 mb-2 leading-tight">{room.type}</h3>
                                                    <div className="flex flex-wrap gap-2 mb-3">
                                                        <span className="inline-flex items-center text-xs font-bold bg-gray-100 text-gray-700 px-2.5 py-1.5 rounded-lg border border-gray-200">
                                                            <Users size={14} className="mr-1.5 text-blue-600" /> Up to {room.capacity}
                                                        </span>
                                                        {room.features.map((f, i) => (
                                                            <span key={i} className="inline-flex items-center text-xs font-bold bg-gray-100 text-gray-700 px-2.5 py-1.5 rounded-lg border border-gray-200">
                                                                {f}
                                                            </span>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div className="flex flex-row items-center justify-between border-t border-dashed border-gray-200 pt-3 mt-2">
                                                    {room.isSoldOut ? (
                                                        <div className="text-[11px] font-bold text-red-600 bg-red-50 px-2.5 py-1.5 rounded-md shrink-0">Sold Out</div>
                                                    ) : (
                                                        <div className="text-[11px] font-bold text-emerald-600 bg-emerald-50 px-2.5 py-1.5 rounded-md shrink-0">Free Cancellation</div>
                                                    )}
                                                    <div className="text-right">
                                                        <div className="text-2xl font-black text-gray-900 tracking-tight leading-none mb-1">₹{room.pricePerNight.toLocaleString('en-IN')}</div>
                                                        <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">per night</div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="bg-gray-50 rounded-3xl p-8 text-center border-2 border-dashed border-gray-200">
                                    <h3 className="text-lg font-bold text-gray-600">No rooms available</h3>
                                    <p className="text-gray-500 mt-2">This property currently has no rooms configured.</p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Right Column: Booking Widget (Desktop) */}
                    <div className="hidden lg:block lg:w-1/3">
                        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-5 sticky top-24">
                            {!selectedRoom ? (
                                <div className="text-center py-8">
                                    <div className="text-gray-300 mb-4 flex justify-center">
                                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>
                                    </div>
                                    <h3 className="font-bold text-gray-700">Room not selected</h3>
                                    <p className="text-sm text-gray-500 mt-2">Please select a room to view pricing and book.</p>
                                </div>
                            ) : (
                                <>
                                    <div className="flex items-baseline mb-2">
                                        <h3 className="text-3xl font-black text-gray-900 tracking-tight">₹{selectedRoom.pricePerNight.toLocaleString('en-IN')}</h3>
                                        <span className="text-gray-500 font-medium ml-2">/ night</span>
                                    </div>
                                    <p className="text-sm font-bold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-lg inline-block mb-4">{selectedRoom.type}</p>

                                    {onlineDiscount.status !== 'inactive' && Number(onlineDiscount.percentage) > 0 && (
                                        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200/80 rounded-2xl p-3 mb-5 flex items-center gap-2.5 shadow-sm">
                                            <div className="w-7 h-7 rounded-xl bg-blue-600 text-white flex items-center justify-center font-black text-xs shrink-0 shadow-sm shadow-blue-500/20">
                                                %
                                            </div>
                                            <div className="text-left leading-tight">
                                                <span className="block text-xs font-bold text-blue-950">
                                                    Online Payment Offer: {onlineDiscount.percentage}% OFF
                                                </span>
                                                <span className="block text-[11px] text-blue-700 font-medium mt-0.5">
                                                    Save up to ₹{onlineDiscount.max_amount} when you pay online!
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                    <div className="border border-gray-200 rounded-2xl mb-6 overflow-hidden shadow-sm">
                                        <div className="p-4 border-b border-gray-200 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => setShowDatePicker(!showDatePicker)}>
                                            <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Dates</div>
                                            <div className="font-semibold text-gray-900 text-sm">
                                                {format(dateRange[0].startDate, 'MMM d, yyyy')} - {format(dateRange[0].endDate, 'MMM d, yyyy')}
                                            </div>
                                        </div>
                                        {showDatePicker && (
                                            <div className="absolute z-30 bg-white border border-gray-100 shadow-2xl rounded-2xl mt-2 p-2 right-6">
                                                <DateRange
                                                    locale={enUS}
                                                    ranges={dateRange}
                                                    onChange={item => setDateRange([item.selection])}
                                                    minDate={new Date()}
                                                    rangeColors={['#2563eb']}
                                                    showDateDisplay={false}
                                                />
                                                <button className="w-full bg-gray-900 text-white font-bold py-2.5 rounded-xl mt-2 hover:bg-gray-800 transition-colors" onClick={() => setShowDatePicker(false)}>Done</button>
                                            </div>
                                        )}
                                        <div className="p-4 flex justify-between items-center bg-white hover:bg-gray-50 transition-colors cursor-pointer" onClick={() => { }}>
                                            <div>
                                                <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">Guests</div>
                                                <div className="font-semibold text-gray-900 text-sm">{guests} Guest{guests > 1 ? 's' : ''}</div>
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <button
                                                    className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${guests <= 1 ? 'border-transparent bg-gray-50 text-gray-300 cursor-not-allowed' : 'border-gray-200 text-gray-600 hover:border-gray-400 hover:bg-gray-100'}`}
                                                    onClick={(e) => { e.stopPropagation(); setGuests(Math.max(1, guests - 1)); }}
                                                    disabled={guests <= 1}
                                                >
                                                    <Minus size={14} strokeWidth={3} />
                                                </button>
                                                <span className="font-bold w-4 text-center text-gray-900">{guests}</span>
                                                <button
                                                    className={`w-8 h-8 rounded-full border-2 flex items-center justify-center transition-colors ${guests >= (selectedRoom?.capacity || 10) ? 'border-transparent bg-gray-50 text-gray-300 cursor-not-allowed' : 'border-gray-200 text-gray-600 hover:border-gray-400 hover:bg-gray-100'}`}
                                                    onClick={(e) => { e.stopPropagation(); setGuests(Math.min(selectedRoom?.capacity || 10, guests + 1)); }}
                                                    disabled={guests >= (selectedRoom?.capacity || 10)}
                                                >
                                                    <Plus size={14} strokeWidth={3} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <button onClick={handleBookNow} disabled={selectedRoom.isSoldOut} className={`w-full font-black py-2 rounded-2xl shadow-lg transition-all mb-6 text-lg tracking-wide ${selectedRoom.isSoldOut ? 'bg-gray-400 text-white cursor-not-allowed shadow-none' : 'bg-blue-600 text-white shadow-blue-600/30 hover:bg-blue-700 hover:shadow-blue-600/50 active:scale-95'}`}>
                                        {selectedRoom.isSoldOut ? 'Sold Out' : 'Book Now'}
                                    </button>

                                    <div className="space-y-3 text-sm">
                                        <div className="flex justify-between text-gray-600 font-medium">
                                            <span>₹{selectedRoom.pricePerNight.toLocaleString('en-IN')} x {totalNights} nights</span>
                                            <span className="font-bold text-gray-900">₹{totalPrice.toLocaleString('en-IN')}</span>
                                        </div>
                                        <div className="flex justify-between text-gray-600 font-medium pb-4 border-b border-gray-200">
                                            <span>Taxes & Fees</span>
                                            <span className="font-bold text-gray-900">₹{taxes.toLocaleString('en-IN')}</span>
                                        </div>
                                        <div className="flex justify-between font-black text-xl text-gray-900 pt-1">
                                            <span>Total</span>
                                            <span>₹{finalAmount.toLocaleString('en-IN')}</span>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Sticky Bottom Booking Bar (Mobile Only) */}
            <div className="lg:hidden fixed bottom-[65px] left-0 right-0 bg-white/90 backdrop-blur-xl border-t border-gray-200 p-4 z-30 shadow-[0_-10px_40px_rgba(0,0,0,0.08)] flex items-center justify-between">
                {selectedRoom ? (
                    <>
                        <div>
                            <div className="flex items-baseline">
                                <span className="text-2xl font-black text-gray-900 tracking-tight">₹{selectedRoom.pricePerNight.toLocaleString('en-IN')}</span>
                                <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider ml-1.5">/ night</span>
                            </div>
                            <div className="text-xs font-semibold text-gray-500 mt-1" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                                {format(dateRange[0].startDate, 'MMM d')} - {format(dateRange[0].endDate, 'MMM d')} • {guests} Guests
                            </div>
                        </div>
                        <button onClick={handleBookNow} disabled={selectedRoom.isSoldOut} className={`font-black py-3 px-8 rounded-2xl shadow-lg active:scale-95 transition-transform text-sm tracking-wide ${selectedRoom.isSoldOut ? 'bg-gray-400 text-white cursor-not-allowed shadow-none' : 'bg-blue-600 text-white shadow-blue-600/30'}`}>
                            {selectedRoom.isSoldOut ? 'Sold Out' : 'Book Now'}
                        </button>
                    </>
                ) : (
                    <div className="flex w-full items-center justify-center">
                        <span className="text-sm font-bold text-gray-500">Please select a room</span>
                    </div>
                )}
            </div>

            {/* Custom Toast Notification */}
            {showToast && (
                <div
                    className="fixed bottom-24 left-1/2 bg-gray-900 text-white px-6 py-3 rounded-full shadow-2xl flex items-center gap-3 z-[60]"
                    style={{ animation: 'fadeInUpToast 0.3s ease-out forwards' }}
                >
                    <CheckCircle size={20} className="text-green-400" />
                    <span className="font-semibold text-sm">Link copied to clipboard!</span>
                </div>
            )}

            <LoginModal 
                isOpen={showLoginModal} 
                onClose={() => setShowLoginModal(false)} 
                onSuccess={() => {
                    setShowLoginModal(false);
                    handleBookNow();
                }} 
            />

            <style dangerouslySetInnerHTML={{
                __html: `
                @keyframes fadeInUpToast {
                    from { opacity: 0; transform: translate(-50%, 20px); }
                    to { opacity: 1; transform: translate(-50%, 0); }
                }
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                .scrollbar-hide {
                    -ms-overflow-style: none;
                    scrollbar-width: none;
                }
            `}} />
        </div>
    );
};

export default HotelDetailsPage;
